<template>
  <VDialogV3
    id="supply-offer-card"
    position="right"
    width="600px"
    :entity="localSupplyOffer"
    :file-entities="fileEntities"
    :file-entities-inherited="fileEntitiesInherited"
    :submit-method="submit"

    @close="onClose"
  >
    <!-- Партнёр -->
    <section>
      <header>Партнер</header>

      <VSelectV2
        ref="seller"
        v-model="localSupplyOffer.seller"
        label="Поставщик"
        :data="({ pattern }) => PartnersService.searchSellers(pattern)"
        :disabled="localSupplyOffer.isExisting"
        required
      />

      <VSelectV2
        v-model="localSupplyOffer.sellerCompany"
        label="Юр. лицо"
        :data="sellerCompanySource"
        :disabled="localSupplyOffer.isExisting"
        required
      />
    </section>

    <hr>

    <!-- Товар -->
    <section>
      <header>Товар</header>

      <VSuggestionSelectV2
        v-model="localSupplyOffer.product"
        label="Товар"
        :data="({ pattern }) => ProductsService.search(pattern)"
        :disabled="localSupplyOffer.isExisting"
        required
      >
        <template v-if="localSupplyOffer.product?.code" #helper-text>
          Артикул: <span>{{ localSupplyOffer.product.code }}</span>
        </template>
      </VSuggestionSelectV2>

      <VInput
        v-model="localSupplyOffer.name"
        label="Наименование товара в ИС поставщика"
        layout="vertical"
        :max-length="175"
      />

      <VSelectV2
        v-model="localSupplyOffer.manufacturer"
        label="Производитель"
        :data="({ pattern }) => ManufacturersService.getList({ pattern })"
        :disabled="localSupplyOffer.isExisting"
        hide-clear-btn
        required
      />

      <VSuggestionSelectV2
        v-model="localSupplyOffer.tradeMark"
        label="Торговая марка"
        :data="({ pattern }) => TradeMarksService.getList({ pattern })"
      />

      <VSelectV2
        v-model="localSupplyOffer.statusAvailability"
        label="Статус доступности товара"
        :data="SupplyOffersService.getStatusesAvailability()"
      />

      <VSwitch
        v-model="localSupplyOffer.isMain"
        theme="slider"
        label="Основной или альтернативный"
        checked-label="Основной"
        unchecked-label="Альтернативный"
      />

      <VInputNumberRusV2
        v-model="localSupplyOffer.minOrderVolume"
        label="Минимальный объем заказа"
        :type="Qty"
      />

      <VInputNumberRus
        v-model="localSupplyOffer.deliveryDaysToStore"
        label="Дни поставки"
        layout="vertical"
        :max-length="4"
      >
        <template #helper-text>
          Укажите срок доставки поставщиком до его склада в днях
        </template>
      </VInputNumberRus>

      <VFormItem ratio="2/1">
        <VSelectV2
          v-model="localSupplyOffer.currency"
          label="Валюта"
          :data="CurrenciesService.currencies"
          :disabled="localSupplyOffer.isExisting"
          hide-clear-btn
          required
        />

        <VSelectV2
          v-model="localSupplyOffer.unit"
          label="Ед. измерения"
          :data="unitSource"
          :disabled="localSupplyOffer.product?.isExisting === true"
          hide-clear-btn
          required
        />
      </VFormItem>

      <VInputNumberRusV2
        v-model="localSupplyOffer.packageVolume"
        label="Фасовка производителя"
        :type="Qty"
        :unit="localSupplyOffer.unit"
        :warning-method="warnPackageVolumeMismatch"
      />
    </section>

    <hr>

    <section>
      <VSwitch
        v-model="localSupplyOffer.isExcludedFromTenders"
        theme="slider"
        :checked-value="false"
        :unchecked-value="true"
        checked-label="Допущен к тендерам"
        unchecked-label="Исключен из тендеров"
      />
      <VHelperText
        v-if="localSupplyOffer.isExcludedFromTenders === true"
        icon="attention"
      >
        Обратите внимание, что при выборе опции <b>"Исключен из тендеров"</b> поставщик не сможет делать ставки в тендерах по этому товару и подавать заявки на участие в них.
      </VHelperText>
    </section>

    <hr>

    <!-- Статус разрешения -->
    <section>
      <label>Статус</label>
      <VButtonGroup>
        <VButton
          v-for="(status, key) in SupplyOffersService.getStatuses()"
          :key="key"
          :theme="getTheme(status)"
          @click="statusChange(status)"
        >
          {{ status.name }}
        </VButton>
      </VButtonGroup>

      <VFormItem
        class="supply-offer-related-url">
        <VInput
          v-model="localSupplyOffer.relatedUrl"
          label="Ссылка"
        >
          <template #helper-text>
            Ссылка на материал, относящиеся к данному разрешению на поставку, например на задачу Jira
          </template>
        </VInput>
        <VButton
          v-if="localSupplyOffer.relatedUrl"
          theme="link"
          title="Копировать ссылку"
          @click="$core.copyToClipboard(localSupplyOffer.relatedUrl)"
        >
          <template #icon>
            <VIcon material="content_copy" theme="outlined"/>
          </template>
        </VButton>
      </VFormItem>

    </section>

    <hr>

    <!-- Бюджетная цена -->
    <BudgetPricesWidget
      v-model:budget-price="budgetPrice"
      :product="localSupplyOffer.product"
    />

    <SupplyOfferTariffsGrid
      :supply-offer="localSupplyOffer"
      :budget-price="budgetPrice"
      @tariff-created="onTariffCreated"
    />
  </VDialogV3>
</template>

<script lang="ts">

import SupplyOffer            from '@/domains/SupplyOffers/SupplyOffer';
import SupplyOffersService    from '@/domains/SupplyOffers/SupplyOffersService';
import SupplyOfferTariffsGrid from '@/domains/SupplyOffers/components/SupplyOfferCard/SupplyOfferTariffsGrid.vue';

import type BudgetPrice   from '@/domains/BudgetPrices/BudgetPrice';
import BudgetPricesWidget from "@/domains/BudgetPrices/components/BudgetPricesWidget/BudgetPricesWidget.vue";
import CompaniesService    from '@/domains/Companies/CompaniesService';
import CurrenciesService   from '@/domains/Currencies/CurrenciesService';
import ManufacturersService from '@/domains/Manufacturers/ManufacturersService';
import PartnersService     from '@/domains/Partners/PartnersService';
import ProductsService     from '@/domains/Products/ProductsService';
import TradeMarksService   from '@/domains/TradeMarks/TradeMarksService';
import UnitsService        from '@/domains/Units/UnitsService';

import EntityPointer from '@/domains/EntityPointer/EntityPointer';

import Qty    from '@/core/types/Qty';
import Mismatch from '@/core/services/Mismatch';


export default defineComponent({
  name: 'SupplyOfferCard',

  components: {
    SupplyOfferTariffsGrid,
    BudgetPricesWidget,
  },

  props: {
    /** Существующее разрешение — карточка грузит его сама */
    id: {
      type:    Number,
      default: null,
    },
    /** Готовое разрешение: заготовка для создания либо строка реестра */
    supplyOffer: {
      type:    Object as PropType<SupplyOffer>,
      default: null,
    },
  },

  setup() {
    return {
      CurrenciesService,
      ManufacturersService,
      PartnersService,
      ProductsService,
      SupplyOffersService,
      TradeMarksService,
      UnitsService,
      Qty,
    };
  },

  data() {
    return {
      localSupplyOffer: undefined as unknown as SupplyOffer,

      // Действующая бюджетная цена из BudgetPricesWidget — здесь нужна только для сетки тарифов
      budgetPrice: null as BudgetPrice | null,

      /** Создание тарифа уже сохранило разрешение — при закрытии хост должен обновиться */
      isTariffCreated: false,

      /** Юр. лица продавца: грузятся по выбранному seller, зависимый источник. */
      sellerCompanySource: this.$core.loader.select<SupplyOffer>(({ seller }) =>
        CompaniesService.getList({ partnerId: seller!.id })
      )
        .dependsOn({ seller: () => this.localSupplyOffer.seller})
        .preloadOnFocus(this.supplyOffer?.isExisting === true)
        .preloadOnMount(this.supplyOffer?.isExisting === false),

      /** Единицы измерения: список статичный, от товара зависит только подстановка его единицы. */
      unitSource: this.$core.loader.select<SupplyOffer>(UnitsService.units.toArray())
        .dependsOn({ product: () => this.localSupplyOffer?.product })
        .default((options, { product }) =>
          options.find(option => option.id === product?.unit?.id)
        ),
    };
  },

  computed: {
    /** Указатели на сущности, к которым цепляются файлы: только само разрешение. Пустые отсеиваются. */
    fileEntities(): EntityPointer[] {
      return EntityPointer.collect([
        EntityPointer.make('supplyOffer', this.localSupplyOffer?.id, this.localSupplyOffer?.toString()),
      ]);
    },

    /** Указатели на сущности, чьи файлы показываются у разрешения только на чтение. Пустые отсеиваются. */
    fileEntitiesInherited(): EntityPointer[] {
      return EntityPointer.collect([
        EntityPointer.make('manufacturer', this.localSupplyOffer?.manufacturer?.id, this.localSupplyOffer?.manufacturer?.name),
      ]);
    },
  },

  async created() {
    await this.init();
  },

  async mounted() {
    /** Сущность так и не появилась (проп не передан / init не отработал) — фокусить нечего */
    if (this.localSupplyOffer === undefined) {
      return;
    }

    /** из-за того что v-show в табах и это нужно для vform, выползает попытка обратится к ref="seller" в момент создания */
    if (this.$refs.seller === undefined) {
      return;
    }

    /** Если это редактирование, то фокус не нужен */
    if (this.localSupplyOffer.isExisting === true) {
      return;
    }

    await this.$nextTick();

    const input = this.$refs.seller.$el.querySelector('input');

    if (input) {
      input.focus();
    }
  },

  methods: {
    /** Расхождение фасовки производителя с требованием производства — предупреждение без блокировки */
    warnPackageVolumeMismatch(value: Qty | null): string | null {
      const required = this.localSupplyOffer.product?.productionRequiredPackageVolume ?? null;

      if (this.$core.utils.isNullOrUndefined(value) === true || required === null) {
        return null;
      } else {
        return Mismatch.getPackageVolumeMismatch(value!, required, this.localSupplyOffer.unit);
      }
    },

    /** Инициализация карточки */
    async init(): Promise<void> {
      /** Разрешение: по id загружаем с бека, если пришел объект с id — все равно грузим с бэка */
      if (this.id) {
        this.localSupplyOffer = await SupplyOffersService.get(this.id);
      }
      else if (this.supplyOffer?.id) {
        this.localSupplyOffer = await SupplyOffersService.get(this.supplyOffer.id);
      }
      else if (this.$core.utils.isNullOrUndefined(this.supplyOffer) === false) {
        this.localSupplyOffer = this.supplyOffer;
      }
    },

    /** Статус может быть не проставлен — тогда ни одна кнопка не подсвечена */
    getTheme(status) {
      if (status.id === this.localSupplyOffer.status?.id) {
        return 'primary';
      }
      else {
        return 'default';
      }
    },

    statusChange(status) {
      this.localSupplyOffer.status = status;
    },

    async onTariffCreated() {
      if (this.localSupplyOffer.isExisting === true) {
        const copy = this.$core.utils.cloneDeep(this.localSupplyOffer);
        await SupplyOffersService.update(copy);
        this.localSupplyOffer = await SupplyOffersService.get(copy.id);
        this.isTariffCreated = true;
      }
    },

    /** Закрытие пользователем: если тариф уже сохранили — resolve, чтобы реестр обновился */
    onClose(): void {
      if (this.isTariffCreated === true) {
        this.$core.dialogs.SupplyOfferCard.resolve();
      }
      else {
        // ничего не сохраняли — диалог закроет сам VDialogV3
      }
    },

    /**
     * Сохранение разрешения. Вызывается диалогом через
     * submitMethod: валидацию, блокировку кнопки и resolve() делает VDialogV3.
     */
    async submit(): Promise<boolean> {
      let success: boolean;

      if (this.localSupplyOffer.isExisting === true) {
        success = await SupplyOffersService.update(this.localSupplyOffer);
      }
      else {
        const created = await SupplyOffersService.add(this.localSupplyOffer);
        success = created !== null;
      }

      return success;
    },
  },
});
</script>
